import Label from '../../components/form/Label';
import Input from '../../components/form/input/InputField';

export const HUMAN_TRANSFER_WEEKDAYS = [
  'Monday',
  'Tuesday',
  'Wednesday',
  'Thursday',
  'Friday',
  'Saturday',
  'Sunday',
] as const;

export type HumanTransferSchedule = 'always' | 'weekly' | 'daily';

export interface HumanTransferDay {
  day_of_week: string;
  start_time: string;
  end_time: string;
  is_closed: boolean;
}

export interface HumanTransferFormFields {
  human_transfer_enabled: boolean;
  human_transfer_schedule: HumanTransferSchedule;
  human_transfer_start_time: string;
  human_transfer_end_time: string;
  human_transfer_hours: HumanTransferDay[];
}

const clockTime = (value: unknown): string => {
  if (typeof value !== 'string' || !value) return '';
  return value.slice(0, 5);
};

export const emptyHumanTransferHours = (): HumanTransferDay[] =>
  HUMAN_TRANSFER_WEEKDAYS.map((day) => ({
    day_of_week: day,
    start_time: '',
    end_time: '',
    is_closed: true,
  }));

export const normalizeHumanTransferHours = (
  value: unknown,
): HumanTransferDay[] => {
  const rows = Array.isArray(value) ? value : [];
  const byDay = new Map(
    rows
      .filter(
        (row): row is Record<string, unknown> =>
          !!row && typeof row === 'object' && 'day_of_week' in row,
      )
      .map((row) => [String(row.day_of_week), row]),
  );
  return HUMAN_TRANSFER_WEEKDAYS.map((day) => {
    const row = byDay.get(day);
    if (!row || row.is_closed) {
      return {
        day_of_week: day,
        start_time: '',
        end_time: '',
        is_closed: true,
      };
    }
    return {
      day_of_week: day,
      start_time: clockTime(row.start_time),
      end_time: clockTime(row.end_time),
      is_closed: false,
    };
  });
};

export const humanTransferFormFields = (
  data?: Record<string, unknown> | null,
): HumanTransferFormFields => {
  const schedule = data?.human_transfer_schedule;
  return {
    human_transfer_enabled: Boolean(data?.human_transfer_enabled),
    human_transfer_schedule:
      schedule === 'weekly' || schedule === 'daily' ? schedule : 'always',
    human_transfer_start_time: clockTime(data?.human_transfer_start_time),
    human_transfer_end_time: clockTime(data?.human_transfer_end_time),
    human_transfer_hours: normalizeHumanTransferHours(
      data?.human_transfer_hours,
    ),
  };
};

export const describeHumanTransferHours = (
  hours: HumanTransferDay[] | undefined,
): string => {
  const open = (hours || []).filter(
    (day) => !day.is_closed && day.start_time && day.end_time,
  );
  if (open.length === 0) return 'No days';
  return open
    .map((day) => `${day.day_of_week} ${day.start_time}–${day.end_time}`)
    .join(', ');
};

export const appendHumanTransferFields = (
  formData: FormData,
  data: Partial<HumanTransferFormFields>,
) => {
  const fields = humanTransferFormFields(data as Record<string, unknown>);
  formData.append(
    'human_transfer_enabled',
    fields.human_transfer_enabled ? 'true' : 'false',
  );
  formData.append('human_transfer_schedule', fields.human_transfer_schedule);
  formData.append(
    'human_transfer_start_time',
    fields.human_transfer_start_time || '',
  );
  formData.append(
    'human_transfer_end_time',
    fields.human_transfer_end_time || '',
  );
  formData.append(
    'human_transfer_hours',
    JSON.stringify(fields.human_transfer_hours),
  );
};

interface HumanTransferScheduleFieldsProps {
  value: Partial<HumanTransferFormFields>;
  onChange: (_patch: Partial<HumanTransferFormFields>) => void;
  /** Store & POS already has the enable checkbox. Only render the hours. */
  embedded?: boolean;
}

export default function HumanTransferScheduleFields({
  value,
  onChange,
  embedded = false,
}: HumanTransferScheduleFieldsProps) {
  const fields = humanTransferFormFields(value as Record<string, unknown>);
  const updateDay = (day: string, patch: Partial<HumanTransferDay>) => {
    onChange({
      human_transfer_hours: fields.human_transfer_hours.map((row) =>
        row.day_of_week === day ? { ...row, ...patch } : row,
      ),
    });
  };

  if (embedded && !fields.human_transfer_enabled) {
    return null;
  }

  return (
    <section
      className={
        embedded
          ? 'mt-5 border-t border-gray-200 pt-5 dark:border-gray-700'
          : 'rounded-2xl border border-gray-200 bg-white/70 p-5 shadow-sm dark:border-gray-700 dark:bg-white/[0.03] md:p-6'
      }
    >
      {!embedded && (
        <h4 className="text-base font-semibold tracking-tight text-gray-900 dark:text-white">
          Human transfer
        </h4>
      )}
      <p
        className={
          embedded
            ? 'text-sm text-gray-500 dark:text-gray-400'
            : 'mt-1 text-sm text-gray-500 dark:text-gray-400'
        }
      >
        Times use the store timezone. Transfer starts at the start time and
        stops at the end time. An earlier end continues past midnight.
      </p>
      {!embedded && (
        <label className="mt-4 flex items-start gap-3 text-sm">
          <input
            type="checkbox"
            name="human_transfer_enabled"
            checked={fields.human_transfer_enabled}
            onChange={(event) =>
              onChange({ human_transfer_enabled: event.target.checked })
            }
            className="mt-0.5 h-4 w-4 shrink-0 rounded border-gray-300 text-brand-600 focus:ring-brand-500"
          />
          <span className="leading-5 text-gray-800 dark:text-gray-100">
            Allow the agent to transfer calls to the store
          </span>
        </label>
      )}
      {fields.human_transfer_enabled && (
        <div
          className={
            embedded
              ? 'mt-4 space-y-4'
              : 'mt-5 space-y-4 border-t border-gray-200 pt-5 dark:border-gray-700'
          }
        >
          <div className="max-w-sm">
            <Label htmlFor="human_transfer_schedule">
              When transfer is available
            </Label>
            <select
              id="human_transfer_schedule"
              name="human_transfer_schedule"
              value={fields.human_transfer_schedule}
              onChange={(event) =>
                onChange({
                  human_transfer_schedule: event.target
                    .value as HumanTransferSchedule,
                })
              }
              className="w-full rounded-lg border border-gray-300 bg-white px-3 py-2.5 text-sm text-gray-900 shadow-sm focus:border-brand-500 focus:outline-none focus:ring-1 focus:ring-brand-500 dark:border-gray-600 dark:bg-gray-800 dark:text-white"
            >
              <option value="always">Any time</option>
              <option value="weekly">Same hours every day</option>
              <option value="daily">Different hours each day</option>
            </select>
          </div>
          {fields.human_transfer_schedule === 'weekly' && (
            <div className="grid grid-cols-1 gap-4 sm:grid-cols-2">
              <div>
                <Label htmlFor="human_transfer_start_time">Start</Label>
                <Input
                  id="human_transfer_start_time"
                  type="time"
                  name="human_transfer_start_time"
                  value={fields.human_transfer_start_time}
                  onChange={(event) =>
                    onChange({ human_transfer_start_time: event.target.value })
                  }
                />
              </div>
              <div>
                <Label htmlFor="human_transfer_end_time">End</Label>
                <Input
                  id="human_transfer_end_time"
                  type="time"
                  name="human_transfer_end_time"
                  value={fields.human_transfer_end_time}
                  onChange={(event) =>
                    onChange({ human_transfer_end_time: event.target.value })
                  }
                />
              </div>
            </div>
          )}
          {fields.human_transfer_schedule === 'daily' && (
            <div className="space-y-3">
              {fields.human_transfer_hours.map((day) => (
                <div
                  key={day.day_of_week}
                  className="grid grid-cols-1 items-end gap-3 sm:grid-cols-[8rem_7rem_1fr_1fr]"
                >
                  <div className="text-sm font-medium text-gray-800 dark:text-gray-100">
                    {day.day_of_week}
                  </div>
                  <label className="flex items-center gap-2 pb-2 text-sm text-gray-700 dark:text-gray-200">
                    <input
                      type="checkbox"
                      checked={!day.is_closed}
                      onChange={(event) =>
                        updateDay(day.day_of_week, {
                          is_closed: !event.target.checked,
                          start_time: event.target.checked
                            ? day.start_time || '09:00'
                            : '',
                          end_time: event.target.checked
                            ? day.end_time || '21:00'
                            : '',
                        })
                      }
                      className="h-4 w-4 rounded border-gray-300 text-brand-600 focus:ring-brand-500"
                    />
                    Open
                  </label>
                  <div>
                    <Label>Start</Label>
                    <Input
                      type="time"
                      value={day.start_time}
                      disabled={day.is_closed}
                      onChange={(event) =>
                        updateDay(day.day_of_week, {
                          start_time: event.target.value,
                        })
                      }
                    />
                  </div>
                  <div>
                    <Label>End</Label>
                    <Input
                      type="time"
                      value={day.end_time}
                      disabled={day.is_closed}
                      onChange={(event) =>
                        updateDay(day.day_of_week, {
                          end_time: event.target.value,
                        })
                      }
                    />
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>
      )}
    </section>
  );
}
